import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { OUTBOUND_TYPE_OPTIONS } from './delivery-order/add/outbound-delivery-order-add.config';

// ============================================================================
// INTERFACES
// ============================================================================

export interface TrackingPolicy {
  requireLot?: boolean;
  requireExpiry?: boolean;
  requireSerial?: boolean;
}

export interface OutboundDeliveryItem {
  id?: string;
  serialNumber?: number;
  spuCode: string; // SPU code
  spuName: string; // SPU name
  skuCode: string; // SKU code
  skuName: string; // SKU name
  quantity: number; // Quantity
  plannedQty?: number; // Planned quantity (số lượng lúc đầu lên kế hoạch sẽ xuất)
  pickedQty?: number; // Picked quantity (số lượng hàng đã lấy - trong page Lấy hàng)
  alreadyOutQty?: number; // Already out quantity (số lượng hàng đã được xuất kho - sau khi confirm shipping)
  waitOutQty?: number; // Wait out quantity (số lượng đang chờ xuất)
  batchId?: string;
  policy?: TrackingPolicy; // Tracking policy for lot/serial requirements
}

export interface StockDeliveryRecord {
  id: string;
  code: string; // Outbound notification number
  platformOrderCode: string; // Third-party order number
  orderCode: string; // Internal order number
  frontRecordCode: string; // Preceding document number
  frontRecordId?: string | number; // Preceding document ID
  outType?: string; // Outbound type code (order-sales-out-stock, transfer-out-stock)
  outTypeMeaning: string; // Outbound type name
  status: string; // Outbound status
  qty: number; // Required shipping quantity
  outLogicWarehouseCode: string; // Outbound logical warehouse code
  outLogicWarehouseName: string; // Outbound logical warehouse name
  receivingLogicWarehouseCode?: string; // Receiving logical warehouse code
  receivingLogicWarehouseName?: string; // Receiving logical warehouse name (kho nhận)
  shopName: string; // Sales store
  channelTypeCodeMeaning: string; // Sales channel
  creationDate: string; // Creation time
  detailList?: OutboundDeliveryItem[]; // Product details
  logisticCompany?: string; // Logistics company
  logisticBusiness?: string; // Logistics business
  alreadyQty?: number; // Quantity already shipped
  scheduledDeliveryTime?: string; // Scheduled delivery time
  pickingStatus?: string; // Picking status: 'NOT_PICKED' | 'PARTIALLY_PICKED' | 'ALL_PICKED' (from API list page)
  receiver?: string; // Receiver
}

export interface StockDeliveryResultRecord {
  id: string;
  resultCode: string; // Outbound result number
  odoCode: string; // Outbound notification number
  odoId?: string; // Outbound delivery order ID (for fetching item details)
  orderCode: string; // Internal order number
  outType?: string; // Outbound type code: 'transfer-out-stock' (Điều chuyển kho) hoặc 'order-sales-out-stock' (Đơn hàng bán)
  outTypeMeaning: string; // Outbound type
  status: string; // Outbound status
  alreadyOutQty: number; // Already shipped quantity
  outLogicWarehouseCode: string; // Outbound logical warehouse code
  outLogicWarehouseName: string; // Outbound logical warehouse name
  shopName: string; // Sales store
  channelTypeCodeMeaning: string; // Sales channel
  creationDate: string; // Creation time
}

export interface StockDeliveryListResponse {
  data: StockDeliveryRecord[];
  totalRecords: number;
  loading: boolean;
}

export interface StockDeliveryResultListResponse {
  data: StockDeliveryResultRecord[];
  totalRecords: number;
  loading: boolean;
}

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const STOCK_DELIVERY_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'queryCode',
    label: 'outbound_management.search_filter.code_or_reference',
    type: 'text',
    placeholder:
      'outbound_management.search_filter.code_or_reference_placeholder',
  },
  {
    key: 'outType',
    label: 'outbound_management.search_filter.outbound_type',
    type: 'select',
    placeholder: 'outbound_management.search_filter.outbound_type_placeholder',
    options: OUTBOUND_TYPE_OPTIONS,
  },
  {
    key: 'status',
    label: 'outbound_management.search_filter.outbound_status',
    type: 'select',
    placeholder:
      'outbound_management.search_filter.outbound_status_placeholder',
    options: [], // Will be populated from API
  },
  {
    key: 'outLogicWarehouseCode',
    label: 'outbound_management.search_filter.outbound_logical_warehouse',
    type: 'select',
    placeholder:
      'outbound_management.search_filter.outbound_logical_warehouse_placeholder',
    options: [], // Will be populated from API
  },
  {
    key: 'creationTime',
    label: 'outbound_management.table.columns.creation_time',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

export const STOCK_DELIVERY_RESULT_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'queryCode',
    label: 'outbound_management.search_filter.order_number',
    type: 'text',
    placeholder: 'Enter order number',
  },
  {
    key: 'outLogicWarehouseCode',
    label: 'outbound_management.search_filter.outbound_logical_warehouse',
    type: 'select',
    placeholder:
      'outbound_management.search_filter.outbound_logical_warehouse_placeholder',
    options: [], // Will be populated from API
  },
  {
    key: 'creationTime',
    label: 'outbound_management.table.columns.creation_time',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

// ============================================================================
// OUTBOUND DELIVERY ORDER TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names for Outbound Delivery Order table
 */
export const STOCK_DELIVERY_DEFAULT_COLUMNS = {
  CODE: 'code',
  REFERENCE_CODE: 'referenceCode',
  OUT_TYPE: 'outType',
  STATUS: 'status',
  OUT_LOGIC_WAREHOUSE_NAME: 'outLogicWarehouseName',
  RECEIVER: 'receiver',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Outbound Delivery Order table
 */
export const STOCK_DELIVERY_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Outbound Delivery Order table
 */
export const STOCK_DELIVERY_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Outbound Delivery Order table
 */
export const STOCK_DELIVERY_DEFAULT_COLUMNS_ORDER: string[] = [
  STOCK_DELIVERY_DEFAULT_COLUMNS.CODE,
  STOCK_DELIVERY_DEFAULT_COLUMNS.REFERENCE_CODE,
  STOCK_DELIVERY_DEFAULT_COLUMNS.OUT_TYPE,
  STOCK_DELIVERY_DEFAULT_COLUMNS.STATUS,
  STOCK_DELIVERY_DEFAULT_COLUMNS.OUT_LOGIC_WAREHOUSE_NAME,
  STOCK_DELIVERY_DEFAULT_COLUMNS.RECEIVER,
  STOCK_DELIVERY_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Outbound Delivery Order table
 */
export const STOCK_DELIVERY_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const STOCK_DELIVERY_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'outbound_management.table.columns.outbound_order_code',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.CODE,
    width: 180,
  },
  {
    label: 'outbound_management.table.columns.reference_order_number',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.REFERENCE_CODE,
    width: 200,
    type: 'custom',
  },
  {
    label: 'outbound_management.table.columns.outbound_type',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.OUT_TYPE,
    width: 150,
    type: 'custom',
  },
  {
    label: 'outbound_management.table.columns.outbound_status',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.STATUS,
    width: 150,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'outbound_management.table.columns.outbound_warehouse_or_store',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.OUT_LOGIC_WAREHOUSE_NAME,
    width: 200,
    type: 'custom',
  },
  {
    label: 'outbound_management.table.columns.receiver',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.RECEIVER,
    width: 150,
  },
  {
    label: 'outbound_management.table.columns.creation_time',
    prop: STOCK_DELIVERY_DEFAULT_COLUMNS.CREATION_DATE,
    width: 140,
    type: 'custom',
    align: 'center',
    sortable: true,
  },
  {
    label: 'outbound_management.table.columns.action',
    prop: STOCK_DELIVERY_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 100,
    align: 'center',
  },
];

// ============================================================================
// OUTBOUND RESULT TABLE CONFIGURATION
// ============================================================================

/**
 * Default column property names for Outbound Result table
 */
export const STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS = {
  RESULT_CODE: 'resultCode',
  ODO_CODE: 'odoCode',
  ORDER_CODE: 'orderCode',
  OUT_TYPE: 'outType',
  ALREADY_OUT_QTY: 'alreadyOutQty',
  OUT_LOGIC_WAREHOUSE_NAME: 'outLogicWarehouseName',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Outbound Result table
 */
export const STOCK_DELIVERY_RESULT_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Outbound Result table
 */
export const STOCK_DELIVERY_RESULT_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Outbound Result table
 */
export const STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS_ORDER: string[] = [
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.RESULT_CODE,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ODO_CODE,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ORDER_CODE,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.OUT_TYPE,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ALREADY_OUT_QTY,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.OUT_LOGIC_WAREHOUSE_NAME,
  STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Outbound Result table
 */
export const STOCK_DELIVERY_RESULT_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const STOCK_DELIVERY_RESULT_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'outbound_management.table.columns.outbound_result_number',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.RESULT_CODE,
    width: 160,
  },
  {
    label: 'outbound_management.table.columns.outbound_notice_number',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ODO_CODE,
    width: 160,
  },
  {
    label: 'outbound_management.table.columns.order_code',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ORDER_CODE,
    width: 170,
  },
  {
    label: 'outbound_management.table.columns.outbound_type',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.OUT_TYPE,
    width: 120,
    type: 'custom',
  },
  {
    label: 'outbound_management.table.columns.shipped_quantity',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.ALREADY_OUT_QTY,
    width: 100,
  },
  {
    label: 'outbound_management.table.columns.outbound_warehouse_or_store',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.OUT_LOGIC_WAREHOUSE_NAME,
    width: 180,
    type: 'custom',
  },
  {
    label: 'outbound_management.table.columns.creation_time',
    prop: STOCK_DELIVERY_RESULT_DEFAULT_COLUMNS.CREATION_DATE,
    width: 140,
    type: 'custom',
    align: 'center',
    sortable: true,
  },
  {
    label: 'outbound_management.table.columns.action',
    prop: STOCK_DELIVERY_RESULT_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 80,
    align: 'center',
  },
];

// ============================================================================
// CONSTANTS
// ============================================================================

export const STOCK_DELIVERY_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
} as const;

export const STOCK_DELIVERY_MESSAGES = {
  ERROR: {
    DATA_LOAD_FAILED: 'Failed to load outbound delivery data',
  },
} as const;
